import type { SrIconName } from "@/components/ui/SrIcon";

/** Validerings-shape. Bruker string for key/parent (ikke MenuKey) for å unngå
 *  sirkularitet med MenuKey-avledningen, men SrIconName for ikon så feil ikon
 *  fanges av `satisfies`. */
interface MenuItemInput {
  key: string;
  label: string;
  parent?: string;
  nav?: { path: string; icon: SrIconName };
}

/**
 * Eneste sannhetspunkt for menyen. Array-rekkefølgen er kanonisk default-
 * rekkefølge (overstyres per rolle av sort_order i role_menu_permissions).
 * - `nav` til stede ⇒ rendres i sidebaren OG har en rute i App.tsx.
 * - `parent` til stede ⇒ undermeny-item.
 * - uten `nav` ⇒ ren RBAC-key for en flate, ikke en side (faktureringsgrad-detalj,
 *   timepris-*, statistlonn-eksport).
 */
export const MENU_REGISTRY = [
  { key: "mine-oppgaver", label: "Mine oppgaver", nav: { path: "/mine-oppgaver", icon: "clock" } },
  // VIB-3022: Chat-rom. Menynøkkelen er seedet på for ansattroller
  // (20261028181411_chat_rom_flag_house_channels.sql); utrullingen styres av
  // flagget feature_chat_rom, som AppLayout og App.tsx sjekker i tillegg.
  { key: "chat", label: "Chat", nav: { path: "/chat", icon: "message-square" } },
  { key: "dashboard", label: "Måltall", nav: { path: "/", icon: "dashboard" } },
  { key: "salgsdashboard", label: "Salgsdashboard", nav: { path: "/dashboard/salg", icon: "trending-up" } },
  { key: "rapporter", label: "Rapporter", nav: { path: "/rapporter", icon: "file-text" } },
  { key: "prosjekter", label: "Prosjekter", nav: { path: "/prosjekter", icon: "folder" } },
  { key: "tilbud", label: "Tilbud", nav: { path: "/tilbud", icon: "file" } },
  // CRM fase 2: kø og pipe. Dark launch, seedet can_access=true kun for
  // superadmin (20261017140000_crm_ko_pipe_flag_menu.sql).
  { key: "salgsmuligheter", label: "Salgsmuligheter", nav: { path: "/salgsmuligheter", icon: "target" } },
  // CRM fase 4: kalde leads. Undernøkkel slik at rollen motebooker kan få bare
  // denne uten resten av Salgsmuligheter (seedet i 20261017180100_crm_motebooker_role_data.sql).
  { key: "salgsmuligheter.leads", label: "Leads", parent: "salgsmuligheter", nav: { path: "/salgsmuligheter/leads", icon: "phone" } },
  // VIB-2988: leadlister og ringeaktivitet, egen tilgang så de som ringer ikke ser dem.
  // Seedet på for superadmin og admin (20261028150035_vib2988_lead_lists_access_manual_leads.sql).
  { key: "salgsmuligheter.leadlister", label: "Leadlister", parent: "salgsmuligheter", nav: { path: "/salgsmuligheter/leadlister", icon: "clipboard-list" } },
  { key: "fakturering", label: "Faktura", nav: { path: "/faktura", icon: "receipt" } },
  // VIB-1289/1290/1291: CRM + løpende avtaler. Dark launch (00-autonom-
  // implementeringsplan.md lanseringsmodell) — menynøklene shippes med
  // can_access=true kun for superadmin (seedet i migrasjon 20260720051750),
  // Erik ruller ut videre via Tilganger og roller.
  { key: "kunder", label: "Kunder", nav: { path: "/kunder", icon: "building-2" } },
  { key: "avtaler", label: "Avtaler", nav: { path: "/avtaler", icon: "repeat" } },
  // VIB-2917: utstyrsdatabasen. Dark launch, seedet can_access=true kun for
  // superadmin (20261024180000_equipment_register.sql); sidene sjekker i
  // tillegg flagget feature_equipment.
  { key: "utstyr", label: "Utstyr", nav: { path: "/utstyr", icon: "package" } },
  // VIB-2938: lokasjonsbanken. Dark launch, seedet can_access=true kun for
  // superadmin (20261025162252_vib2938_location_bank.sql); sidene sjekker i
  // tillegg flagget feature_locations.
  { key: "lokasjoner", label: "Lokasjoner", nav: { path: "/lokasjoner", icon: "map-pin" } },
  { key: "ansatte", label: "Ansatte", nav: { path: "/ansatte", icon: "users" } },
  // VIB-2925: frilanserbanken. Dark launch, seedet can_access=true kun for superadmin
  // (20261024152206_vib2925_freelancer_bank.sql).
  { key: "frilansere", label: "Frilansere", nav: { path: "/frilansere", icon: "film" } },
  { key: "timebank", label: "Timebank", nav: { path: "/timebank", icon: "clock" } },
  { key: "display", label: "Display", nav: { path: "/display", icon: "monitor" } },
  { key: "laering", label: "Læring", nav: { path: "/laering", icon: "graduation-cap" } },
  { key: "innstillinger", label: "Innstillinger", nav: { path: "/innstillinger", icon: "settings" } },
  { key: "innstillinger.varsler", label: "Varsler", parent: "innstillinger", nav: { path: "/innstillinger/varsler", icon: "bell" } },
  { key: "innstillinger.organisasjon", label: "Organisasjon", parent: "innstillinger", nav: { path: "/innstillinger/organisasjon", icon: "building" } },
  { key: "innstillinger.tilganger", label: "Tilganger & roller", parent: "innstillinger", nav: { path: "/innstillinger/tilganger", icon: "shield" } },
  { key: "innstillinger.brukere", label: "Brukere", parent: "innstillinger", nav: { path: "/innstillinger/brukere", icon: "users" } },
  { key: "innstillinger.widgets", label: "Widgets", parent: "innstillinger", nav: { path: "/innstillinger/widgets", icon: "layout-grid" } },
  { key: "innstillinger.okonomi", label: "Økonomi", parent: "innstillinger", nav: { path: "/innstillinger/okonomi", icon: "coins" } },
  { key: "innstillinger.integrasjoner", label: "Integrasjoner", parent: "innstillinger", nav: { path: "/innstillinger/integrasjoner", icon: "plug" } },
  { key: "innstillinger.drift", label: "Drift", parent: "innstillinger", nav: { path: "/innstillinger/drift", icon: "wrench" } },
  { key: "feedback", label: "Feedback", nav: { path: "/feedback", icon: "megaphone" } },
  { key: "faktureringsgrad-detalj", label: "Faktureringsgrad-detalj (drill-down)" },
  { key: "timepris-selger", label: "Reell timepris per selger" },
  { key: "timepris-prosjektleder", label: "Reell timepris per prosjektleder" },
  { key: "statistlonn-eksport", label: "Statistlønn (CSV-eksport)" },
] as const satisfies readonly MenuItemInput[];

export type MenuKey = (typeof MENU_REGISTRY)[number]["key"];

/**
 * Grupper der forelderens egen side også er en lenke inne i gruppen (forelderen
 * er ikke bare en beholder slik som innstillinger). Verdien er lenketeksten.
 * Salgsmuligheter har Leads som barn, men Oppdag og Pipe ligger på forelderen.
 */
export const GROUP_OWN_PAGE_LABELS: Partial<Record<string, string>> = {
  salgsmuligheter: "Oppdag og pipe",
};

/** Full item-type med MenuKey, for avleder-logikk. */
export interface MenuItem {
  key: MenuKey;
  label: string;
  parent?: MenuKey;
  nav?: { path: string; icon: SrIconName };
}

export interface NavConfigEntry {
  path: string;
  label: string;
  icon: SrIconName;
  childKeys?: MenuKey[];
}

export interface MenuKeyDef {
  key: MenuKey;
  label: string;
  subs?: MenuKeyDef[];
}

const ITEMS: readonly MenuItem[] = MENU_REGISTRY as readonly MenuItem[];

/** key → label for ALLE keys. */
export const MENU_LABELS = Object.fromEntries(
  ITEMS.map((m) => [m.key, m.label]),
) as Record<MenuKey, string>;

/** Top-level nav-keys i kanonisk rekkefølge (sidebar + rekkefølge-UI). */
export const NAV_TOP_LEVEL_KEYS: MenuKey[] = ITEMS
  .filter((m) => m.nav && !m.parent)
  .map((m) => m.key);

/** Samme regel som sidebaren — orderbare top-level-punkter. */
export const ORDERABLE_TOP_LEVEL_KEYS: MenuKey[] = NAV_TOP_LEVEL_KEYS;

/** Nav-subkeys under en parent (f.eks. innstillinger.*). */
export function subKeysOf(parent: MenuKey): MenuKey[] {
  return ITEMS.filter((m) => m.parent === parent && m.nav).map((m) => m.key);
}

/** Sidebar-config: alle nav-items; parent-items får childKeys. */
export const NAV_CONFIG: Partial<Record<MenuKey, NavConfigEntry>> =
  Object.fromEntries(
    ITEMS.filter((m) => m.nav).map((m) => {
      const childKeys = ITEMS
        .filter((c) => c.parent === m.key && c.nav)
        .map((c) => c.key);
      const entry: NavConfigEntry = {
        path: m.nav!.path,
        label: m.label,
        icon: m.nav!.icon,
      };
      if (childKeys.length) entry.childKeys = childKeys;
      return [m.key, entry];
    }),
  ) as Partial<Record<MenuKey, NavConfigEntry>>;

/** RBAC-matrise: ALLE keys. Top-level som rad, parent-match som subs. */
export const MENU_HIERARCHY: MenuKeyDef[] = ITEMS
  .filter((m) => !m.parent)
  .map((m) => {
    const subs = ITEMS
      .filter((s) => s.parent === m.key)
      .map((s) => ({ key: s.key, label: s.label }));
    return subs.length
      ? { key: m.key, label: m.label, subs }
      : { key: m.key, label: m.label };
  });
